/**
 * API Service - Centralized API calls to backend
 * With offline support for read operations
 */

import { saveOfflineData, getOfflineData, OFFLINE_STORES } from './offlineStorage';

// Get API URL - MUST be set via environment variable
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (!envUrl || envUrl.trim() === '') {
    console.error('REACT_APP_BACKEND_URL is not configured');
    return '';
  }
  return envUrl;
};
const API_URL = getApiUrl();

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Check if we're offline
const isOffline = () => !navigator.onLine;

// Helper for API calls with offline support
const apiCall = async (endpoint, options = {}, cacheConfig = null) => {
  try {
    // If offline and this is a GET request with caching enabled
    if (isOffline() && (!options.method || options.method === 'GET')) {
      if (cacheConfig) {
        const cachedData = await getOfflineData(cacheConfig.store, cacheConfig.key);
        if (cachedData) {
          return cachedData;
        }
      }
      throw new Error('You are offline and no cached data is available');
    }
    
    // If offline and trying to modify data
    if (isOffline() && options.method && options.method !== 'GET') {
      throw new Error('OFFLINE_WRITE_BLOCKED');
    }
    
    // Add cache busting and no-cache headers
    const url = new URL(`${API_URL}${endpoint}`);
    
    // Add timestamp to prevent browser caching (cache busting)
    url.searchParams.append('_t', Date.now());
    
    const response = await fetch(url.toString(), {
      ...options,
      headers: {
        ...getAuthHeaders(),
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers
      },
      cache: 'no-store' // Tell browser not to use cache
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        console.warn('Authentication failed - token may be expired');
        // Don't redirect if offline session
        const isOfflineSession = localStorage.getItem('isOfflineSession');
        if (!isOfflineSession) {
          // Try to get the actual error message from response
          let errorMessage = 'Session expired. Please login again.';
          try {
            const errorData = await response.json();
            if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            // Use default message
          }
          
          // Store the logout reason for login page to display
          sessionStorage.setItem('logoutReason', errorMessage);
          
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.href = '/login';
        }
        throw new Error('Session expired. Please login again.');
      }

      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `API Error: ${response.status}`);
    }

    const data = await response.json();
    
    // Cache the response if caching is configured (only for GET requests)
    if (cacheConfig && (!options.method || options.method === 'GET')) {
      await saveOfflineData(cacheConfig.store, cacheConfig.key, data);
    }
    
    return data;
  } catch (error) {
    // If network error and we have cache, return cached data
    if (error.message?.includes('fetch') || error.message?.includes('network') || !navigator.onLine) {
      if (cacheConfig) {
        const cachedData = await getOfflineData(cacheConfig.store, cacheConfig.key);
        if (cachedData) {
          return cachedData;
        }
      }
    }
    console.error(`API call failed: ${endpoint}`, error);
    throw error;
  }
};

// ============= BOOKINGS =============
export const bookingsAPI = {
  getAll: () => {
    const userId = JSON.parse(localStorage.getItem('currentUser') || '{}').id || 'default';
    return apiCall('/api/bookings', {}, { 
      store: OFFLINE_STORES.BOOKINGS, 
      key: `bookings_${userId}` 
    });
  },
  
  create: (bookingData) => apiCall('/api/bookings', {
    method: 'POST',
    body: JSON.stringify(bookingData)
  }),
  
  update: (id, bookingData) => apiCall(`/api/bookings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(bookingData)
  }),
  
  delete: (id) => apiCall(`/api/bookings/${id}`, {
    method: 'DELETE'
  }),
  
  addPayment: (id, paymentData) => apiCall(`/api/bookings/${id}/payments`, {
    method: 'POST',
    body: JSON.stringify(paymentData)
  }),
  
  updatePayment: (bookingId, paymentId, paymentData) => apiCall(`/api/bookings/${bookingId}/payments/${paymentId}`, {
    method: 'PUT',
    body: JSON.stringify(paymentData)
  }),
  
  deletePayment: (bookingId, paymentId) => apiCall(`/api/bookings/${bookingId}/payments/${paymentId}`, {
    method: 'DELETE'
  })
};

// ============= LEADS =============
export const leadsAPI = {
  getAll: () => {
    const userId = JSON.parse(localStorage.getItem('currentUser') || '{}').id || 'default';
    return apiCall('/api/leads', {}, { 
      store: OFFLINE_STORES.LEADS, 
      key: `leads_${userId}` 
    });
  },
  
  create: (leadData) => apiCall('/api/leads', {
    method: 'POST',
    body: JSON.stringify(leadData)
  }),
  
  update: (id, leadData) => apiCall(`/api/leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(leadData)
  }),
  
  delete: (id) => apiCall(`/api/leads/${id}`, {
    method: 'DELETE'
  }),
  
  convertToBooking: (id, bookingData) => apiCall(`/api/leads/${id}/convert`, {
    method: 'POST',
    body: JSON.stringify(bookingData)
  })
};

// ============= VENUES =============
export const venuesAPI = {
  getAll: () => {
    const userId = JSON.parse(localStorage.getItem('currentUser') || '{}').id || 'default';
    return apiCall('/api/venues', {}, { 
      store: OFFLINE_STORES.INVENTORY, 
      key: `venues_${userId}` 
    });
  },
  
  getLimitInfo: () => apiCall('/api/venues/limit/info'),
  
  create: (venueData) => apiCall('/api/venues', {
    method: 'POST',
    body: JSON.stringify(venueData)
  }),
  
  update: (id, venueData) => apiCall(`/api/venues/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(venueData)
  }),
  
  delete: (id) => apiCall(`/api/venues/${id}`, {
    method: 'DELETE'
  })
};

// ============= INVENTORY =============
export const inventoryAPI = {
  getAll: () => {
    const userId = JSON.parse(localStorage.getItem('currentUser') || '{}').id || 'default';
    return apiCall('/api/inventory', {}, { 
      store: OFFLINE_STORES.INVENTORY, 
      key: `inventory_${userId}` 
    });
  },
  
  create: (itemData) => apiCall('/api/inventory', {
    method: 'POST',
    body: JSON.stringify(itemData)
  }),
  
  update: (id, itemData) => apiCall(`/api/inventory/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(itemData)
  }),
  
  delete: (id) => apiCall(`/api/inventory/${id}`, {
    method: 'DELETE'
  }),
  
  // Category management
  getCategories: () => apiCall('/api/inventory/categories'),
  
  createCategory: (categoryName) => apiCall('/api/inventory/categories', {
    method: 'POST',
    body: JSON.stringify({ name: categoryName })
  }),
  
  updateCategory: (oldName, newName) => apiCall(`/api/inventory/categories/${encodeURIComponent(oldName)}`, {
    method: 'PUT',
    body: JSON.stringify({ name: newName })
  }),
  
  deleteCategory: (categoryName) => apiCall(`/api/inventory/categories/${encodeURIComponent(categoryName)}`, {
    method: 'DELETE'
  })
};

// ============= SUBSCRIPTION =============
export const subscriptionAPI = {
  requestSubscription: (userEmail, plan = 'PLAN_BASIC') => apiCall('/api/auth/request-subscription', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_email: userEmail, plan })
  }),
  
  activateFreeTrial: (userEmail) => apiCall('/api/auth/free-trial', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_email: userEmail })
  }),
  
  getAccessLevel: () => apiCall('/api/subscriptions/access-level'),
  
  cancelSubscription: () => apiCall('/api/subscriptions/cancel', {
    method: 'POST'
  }),
  
  getStatus: () => apiCall('/api/subscriptions/status')
};

// ============= PROFILE SETTINGS =============
export const profileAPI = {
  toggleFinancials: (password, hideFinancials) => apiCall('/api/auth/toggle-financials', {
    method: 'POST',
    body: JSON.stringify({ password, hideFinancials })
  }),
  
  toggleStaffFinancials: (password, hideFinancialsFromStaff) => apiCall('/api/auth/toggle-staff-financials', {
    method: 'POST',
    body: JSON.stringify({ password, hideFinancialsFromStaff })
  }),
  
  getAdminSettings: () => apiCall('/api/auth/admin-settings')
};

// ============= MASTER/ADMIN =============
export const masterAPI = {
  getAllUsers: () => apiCall('/api/master/users'),
  
  updateUser: (userId, userData) => apiCall(`/api/master/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(userData)
  }),
  
  deleteUser: (userId) => apiCall(`/api/master/users/${userId}`, {
    method: 'DELETE'
  }),
  
  getPendingSubscriptions: () => apiCall('/api/master/subscriptions/pending'),
  
  approveSubscription: (userId, plan, duration) => apiCall('/api/master/subscriptions/approve', {
    method: 'POST',
    body: JSON.stringify({ userId, plan, duration })
  }),
  
  rejectSubscription: (userId, reason) => apiCall('/api/master/subscriptions/reject', {
    method: 'POST',
    body: JSON.stringify({ userId, reason })
  }),
  
  // Venue limit management
  getVenueLimits: () => apiCall('/api/master/venue-limits'),
  
  updateVenueLimit: (userId, maxVenueLimit) => apiCall('/api/master/venue-limit/update', {
    method: 'POST',
    body: JSON.stringify({ userId, maxVenueLimit })
  }),
  
  // Panic mode management
  getPanicModeSettings: () => apiCall('/api/master/panic-mode-settings'),
  
  togglePanicMode: (userId, enabled) => apiCall('/api/master/toggle-panic-mode', {
    method: 'POST',
    body: JSON.stringify({ userId, enabled })
  })
};

// ============= TEAM/USERS =============
export const usersAPI = {
  // Get all team members (staff/managers under current admin)
  getTeamMembers: () => apiCall('/api/users'),
  
  // Create new team member
  createTeamMember: (memberData) => apiCall('/api/users', {
    method: 'POST',
    body: JSON.stringify(memberData)
  }),
  
  // Get user by ID
  getUser: (userId) => apiCall(`/api/users/${userId}`),
  
  // Update user
  updateUser: (userId, userData) => apiCall(`/api/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(userData)
  }),
  
  // Delete user
  deleteUser: (userId) => apiCall(`/api/users/${userId}`, {
    method: 'DELETE'
  })
};

// ============= ACTIVITY LOG =============
export const activityAPI = {
  // Get activity logs (admin only)
  getLogs: (limit = 50, entityType = null, action = null) => {
    let url = `/api/activity?limit=${limit}`;
    if (entityType) url += `&entity_type=${entityType}`;
    if (action) url += `&action=${action}`;
    return apiCall(url);
  },
  
  // Get activity summary
  getSummary: () => apiCall('/api/activity/summary'),
  
  // Delete a single activity log
  delete: (id) => apiCall(`/api/activity/${id}`, {
    method: 'DELETE'
  }),
  
  // Clear all activity logs
  clearAll: () => apiCall('/api/activity/clear-all', {
    method: 'DELETE'
  })
};

// ============= NOTIFICATIONS =============
export const notificationsAPI = {
  getAll: () => apiCall('/api/notifications'),
  
  getUnreadCount: () => apiCall('/api/notifications/unread-count'),
  
  markAsRead: (id) => apiCall(`/api/notifications/${id}/read`, {
    method: 'POST'
  }),
  
  markAllAsRead: () => apiCall('/api/notifications/mark-all-read', {
    method: 'POST'
  }),
  
  delete: (id) => apiCall(`/api/notifications/${id}`, {
    method: 'DELETE'
  }),
  
  clearAll: () => apiCall('/api/notifications/clear-all', {
    method: 'DELETE'
  }),
  
  // Master only - send to specific user
  sendToUser: (userId, title, message) => apiCall(
    `/api/notifications/send?user_id=${encodeURIComponent(userId)}&title=${encodeURIComponent(title)}&message=${encodeURIComponent(message)}`,
    { method: 'POST' }
  ),
  
  // Master only - send mass notification
  sendMass: (title, message, target = 'all') => apiCall(
    `/api/notifications/send-mass?title=${encodeURIComponent(title)}&message=${encodeURIComponent(message)}&target=${target}`,
    { method: 'POST' }
  )
};

export default {
  bookings: bookingsAPI,
  leads: leadsAPI,
  venues: venuesAPI,
  inventory: inventoryAPI,
  subscription: subscriptionAPI,
  master: masterAPI,
  users: usersAPI,
  activity: activityAPI,
  notifications: notificationsAPI
};

// ============= SESSIONS =============
export const sessionsAPI = {
  // Get all active sessions
  getAll: () => apiCall('/api/sessions'),
  
  // Get current session info
  getCurrent: () => apiCall('/api/sessions/current'),
  
  // Logout from a specific session
  logout: (sessionId) => apiCall(`/api/sessions/${sessionId}`, {
    method: 'DELETE'
  }),
  
  // Logout from all other devices
  logoutAllOthers: () => apiCall('/api/sessions', {
    method: 'DELETE'
  })
};


// OTP API
export const otpAPI = {
  send: (email, purpose, name = "User") => apiCall("/api/otp/send", {
    method: "POST",
    body: JSON.stringify({ email, purpose, name })
  }),
  
  verify: (email, otp, purpose) => apiCall("/api/otp/verify", {
    method: "POST",
    body: JSON.stringify({ email, otp, purpose })
  }),
  
  checkStatus: (email, purpose) => apiCall(`/api/otp/status/${encodeURIComponent(email)}/${purpose}`)
};

// Password Reset API
export const passwordResetAPI = {
  resetWithOTP: (email, new_password) => apiCall("/api/auth/reset-password-otp", {
    method: "POST",
    body: JSON.stringify({ email, new_password })
  })
};

// Email Change API
export const emailChangeAPI = {
  changeEmail: (new_email) => apiCall("/api/users/change-email", {
    method: "POST",
    body: JSON.stringify({ new_email })
  })
};

// Audit Log API
export const auditAPI = {
  getLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.action) queryParams.append('action', params.action);
    if (params.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return apiCall(`/api/audit${queryString ? `?${queryString}` : ''}`);
  },
  
  getStats: (days = 7) => apiCall(`/api/audit/stats?days=${days}`),
  
  exportLogs: (startDate, endDate, format = 'json') => 
    apiCall(`/api/audit/export?start_date=${startDate}&end_date=${endDate}&format=${format}`),
  
  clearOld: (daysToKeep = 90) => apiCall(`/api/audit/clear?days_to_keep=${daysToKeep}`, {
    method: 'DELETE'
  })
};

// ============= EXPENSES =============
export const expensesAPI = {
  // Categories
  getCategories: () => apiCall('/api/expenses/categories'),
  
  createCategory: (categoryData) => apiCall('/api/expenses/categories', {
    method: 'POST',
    body: JSON.stringify(categoryData)
  }),
  
  updateCategory: (id, categoryData) => apiCall(`/api/expenses/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(categoryData)
  }),
  
  deleteCategory: (id) => apiCall(`/api/expenses/categories/${id}`, {
    method: 'DELETE'
  }),
  
  // Expenses
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.venueId) params.append('venue_id', filters.venueId);
    if (filters.categoryId) params.append('category_id', filters.categoryId);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.paymentMethod) params.append('payment_method', filters.paymentMethod);
    const query = params.toString();
    return apiCall(`/api/expenses${query ? `?${query}` : ''}`);
  },
  
  getByVenue: (venueId) => apiCall(`/api/expenses/by-venue/${venueId}`),
  
  create: (expenseData) => apiCall('/api/expenses', {
    method: 'POST',
    body: JSON.stringify(expenseData)
  }),
  
  update: (id, expenseData) => apiCall(`/api/expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(expenseData)
  }),
  
  delete: (id) => apiCall(`/api/expenses/${id}`, {
    method: 'DELETE'
  }),
  
  // Reports
  getSummary: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.venueId) params.append('venue_id', filters.venueId);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    const query = params.toString();
    return apiCall(`/api/expenses/reports/summary${query ? `?${query}` : ''}`);
  },
  
  getMonthlyReport: (year, month, venueId = null) => {
    const params = venueId ? `?venue_id=${venueId}` : '';
    return apiCall(`/api/expenses/reports/monthly/${year}/${month}${params}`);
  }
};

// ============= ACCOUNTS =============
export const accountsAPI = {
  // Account Heads
  getAccountHeads: () => apiCall('/api/accounts/heads'),
  getAccountHeadsForBooking: () => apiCall('/api/accounts/heads/for-booking'),
  getAccountTransactions: (accountId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    const query = queryParams.toString();
    return apiCall(`/api/accounts/heads/${accountId}/transactions${query ? `?${query}` : ''}`);
  },
  
  createAccountHead: (accountData) => apiCall('/api/accounts/heads', {
    method: 'POST',
    body: JSON.stringify(accountData)
  }),
  
  updateAccountHead: (id, accountData) => apiCall(`/api/accounts/heads/${id}`, {
    method: 'PUT',
    body: JSON.stringify(accountData)
  }),
  
  deleteAccountHead: (id) => apiCall(`/api/accounts/heads/${id}`, {
    method: 'DELETE'
  }),
  
  recalculateAllBalances: () => apiCall('/api/accounts/heads/recalculate-all', {
    method: 'POST'
  }),
  
  // Income
  getIncome: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.accountHeadId) params.append('account_head_id', filters.accountHeadId);
    if (filters.venueId) params.append('venue_id', filters.venueId);
    if (filters.source) params.append('source', filters.source);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    const query = params.toString();
    return apiCall(`/api/accounts/income${query ? `?${query}` : ''}`);
  },
  
  createIncome: (incomeData) => apiCall('/api/accounts/income', {
    method: 'POST',
    body: JSON.stringify(incomeData)
  }),
  
  updateIncome: (id, incomeData) => apiCall(`/api/accounts/income/${id}`, {
    method: 'PUT',
    body: JSON.stringify(incomeData)
  }),
  
  deleteIncome: (id) => apiCall(`/api/accounts/income/${id}`, {
    method: 'DELETE'
  }),
  
  // Summary & Reports
  getSummary: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    const query = params.toString();
    return apiCall(`/api/accounts/summary${query ? `?${query}` : ''}`);
  },
  
  getTransactions: (accountHeadId, filters = {}) => {
    const params = new URLSearchParams();
    params.append('account_head_id', accountHeadId);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.transactionType) params.append('transaction_type', filters.transactionType);
    return apiCall(`/api/accounts/transactions?${params.toString()}`);
  },
  
  getReport: (startDate, endDate, accountHeadId = null, transactionType = 'all') => {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    if (accountHeadId) params.append('account_head_id', accountHeadId);
    if (transactionType) params.append('transaction_type', transactionType);
    return apiCall(`/api/accounts/reports?${params.toString()}`);
  },
  
  // Transfers (Contra Entries)
  getTransfers: () => apiCall('/api/accounts/transfers'),
  createTransfer: (data) => apiCall('/api/accounts/transfers', { method: 'POST', body: JSON.stringify(data) }),
  deleteTransfer: (id) => apiCall(`/api/accounts/transfers/${id}`, { method: 'DELETE' })
};

export const invoicesAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.venueId) queryParams.append('venue_id', params.venueId);
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return apiCall(`/api/invoices/${query ? `?${query}` : ''}`);
  },
  get: (invoiceId) => apiCall(`/api/invoices/${invoiceId}`),
  create: (invoiceData) => apiCall('/api/invoices/', {
    method: 'POST',
    body: JSON.stringify(invoiceData)
  }),
  update: (invoiceId, invoiceData) => apiCall(`/api/invoices/${invoiceId}`, {
    method: 'PUT',
    body: JSON.stringify(invoiceData)
  }),
  delete: (invoiceId) => apiCall(`/api/invoices/${invoiceId}`, { method: 'DELETE' }),
  markSent: (invoiceId) => apiCall(`/api/invoices/${invoiceId}/mark-sent`, { method: 'POST' }),
  markPaid: (invoiceId, paymentDate, paymentMethod) => {
    const params = new URLSearchParams();
    if (paymentDate) params.append('payment_date', paymentDate);
    if (paymentMethod) params.append('payment_method', paymentMethod);
    return apiCall(`/api/invoices/${invoiceId}/mark-paid?${params.toString()}`, { method: 'POST' });
  },
  markCancelled: (invoiceId, reason) => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    return apiCall(`/api/invoices/${invoiceId}/mark-cancelled${params}`, { method: 'POST' });
  },
  getPDF: (invoiceId) => apiCall(`/api/invoices/${invoiceId}/pdf`),
  getShareLink: (invoiceId) => apiCall(`/api/invoices/${invoiceId}/share-link`),
  getWhatsAppLink: (invoiceId) => apiCall(`/api/invoices/${invoiceId}/whatsapp-link`),
  getSharedInvoice: (shareToken) => apiCall(`/api/invoices/shared/${shareToken}`),
  getTemplates: () => apiCall('/api/invoices/templates/list'),
  createTemplate: (templateData) => apiCall('/api/invoices/templates', {
    method: 'POST',
    body: JSON.stringify(templateData)
  }),
  updateTemplate: (templateId, templateData) => apiCall(`/api/invoices/templates/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify(templateData)
  }),
  deleteTemplate: (templateId) => apiCall(`/api/invoices/templates/${templateId}`, { method: 'DELETE' }),
  fetchCustomerFromBooking: (bookingId) => apiCall(`/api/invoices/fetch-customer/${bookingId}`),
  getNextNumber: () => apiCall('/api/invoices/next-number'),
  getSummary: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    const query = queryParams.toString();
    return apiCall(`/api/invoices/stats/summary${query ? `?${query}` : ''}`);
  },
  getSettings: () => apiCall('/api/invoices/settings/get'),
  updateSettings: (settingsData) => apiCall('/api/invoices/settings/update', {
    method: 'PUT',
    body: JSON.stringify(settingsData)
  }),
  uploadSettingsImage: async (file, category) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${apiUrl}/api/invoices/settings/upload-image?category=${category}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  },
  getSettingsImageUrl: (path) => `${process.env.REACT_APP_BACKEND_URL}/api/invoices/settings/image/${path}`
};

// ============= QUOTATIONS =============
export const quotationsAPI = {
  moduleStatus: () => apiCall('/api/quotations/module-status'),
  getAll: (params = {}) => {
    const q = new URLSearchParams();
    if (params.leadId) q.append('lead_id', params.leadId);
    if (params.venueId) q.append('venue_id', params.venueId);
    if (params.status) q.append('status_filter', params.status);
    if (params.limit) q.append('limit', params.limit);
    if (params.skip) q.append('skip', params.skip);
    const query = q.toString();
    return apiCall(`/api/quotations/${query ? `?${query}` : ''}`);
  },
  get: (id) => apiCall(`/api/quotations/${id}`),
  create: (data) => apiCall('/api/quotations/', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiCall(`/api/quotations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiCall(`/api/quotations/${id}`, { method: 'DELETE' }),
  markSent: (id) => apiCall(`/api/quotations/${id}/send`, { method: 'POST' }),
  accept: (id) => apiCall(`/api/quotations/${id}/accept`, { method: 'POST' }),
  reject: (id) => apiCall(`/api/quotations/${id}/reject`, { method: 'POST' }),
  convertToBooking: (id) => apiCall(`/api/quotations/${id}/convert-to-booking`, { method: 'POST' }),
  getPdfData: (id) => apiCall(`/api/quotations/${id}/pdf`),
  getWhatsAppLink: (id) => apiCall(`/api/quotations/${id}/share/whatsapp`),
  uploadItemImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/upload-item-image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(err.detail || 'Upload failed');
    }
    return res.json();
  },
  getItemImageUrl: (path) => `${process.env.REACT_APP_BACKEND_URL}/api/quotations/item-image/${path}`
};

